import { cva, type VariantProps } from 'class-variance-authority';
import type * as React from 'react';
import { cn } from '../../../common/functions/cn';
import { Button } from '../../button/button';
import { Input } from '../input/input';

const InputGroup = ({
  className,
  size = 'default',
  ...props
}: React.ComponentProps<'div'> & { size?: 'sm' | 'default' }) => {
  return (
    <div
      data-slot="input-group"
      data-size={size}
      role="group"
      className={cn(
        'group/input-group relative flex w-full min-w-0 items-center',
        size === 'sm' ? 'h-7' : 'h-8',
        'rounded-md border border-input bg-background outline-none',
        'has-[[data-slot=input-group-control]:focus-visible]:border-ring',
        'has-[[data-slot=input-group-control]:focus-visible]:ring-2 has-[[data-slot=input-group-control]:focus-visible]:ring-ring/50',
        'has-[[data-slot][aria-invalid=true]]:border-error',
        'has-[[data-slot][aria-invalid=true]]:ring-2 has-[[data-slot][aria-invalid=true]]:ring-error/20',
        'has-[[data-slot][aria-invalid=true]]:has-[[data-slot=input-group-control]:focus-visible]:ring-error/60',
        'has-disabled:bg-muted has-disabled:opacity-50',
        'in-data-[slot=combobox-content]:focus-within:border-inherit in-data-[slot=combobox-content]:focus-within:ring-0',
        'has-[>[data-align=block-end]]:h-auto has-[>[data-align=block-start]]:h-auto',
        'has-[>[data-align=block-end]]:flex-col has-[>[data-align=block-start]]:flex-col',
        'data-[size=sm]:[&>input]:px-2',
        'has-[>[data-align=inline-end]]:[&>input]:pr-1.5 has-[>[data-align=inline-start]]:[&>input]:pl-1.5',
        'has-[>[data-align=block-start]]:[&>input]:pt-3 has-[>[data-align=block-end]]:[&>input]:pb-3',
        className
      )}
      {...props}
    />
  );
};

const inputGroupAddonVariants = cva(
  [
    'flex h-auto items-center justify-center py-1.5',
    'cursor-text select-none text-muted-foreground text-sm',
    'group-data-[disabled=true]/input-group:opacity-50',
    "[&>svg:not([class*='size-'])]:size-4",
  ],
  {
    variants: {
      align: {
        'inline-start': 'order-first py-0 pl-2 has-[>button]:pl-[0.2rem] has-[>kbd]:pl-[0.35rem]',
        'inline-end': 'order-last py-0 pr-2 has-[>button]:pr-[0.2rem] has-[>kbd]:pr-[0.35rem]',
        'block-start':
          'order-first w-full justify-start px-2.5 pt-2 group-has-[>input]/input-group:pt-2 [.border-b]:pb-2',
        'block-end': 'order-last w-full justify-start px-2.5 pb-2 group-has-[>input]/input-group:pb-2 [.border-t]:pt-2',
      },
    },
    defaultVariants: {
      align: 'inline-start',
    },
  }
);

const InputGroupAddon = ({
  className,
  align = 'inline-start',
  ...props
}: React.ComponentProps<'div'> & VariantProps<typeof inputGroupAddonVariants>) => {
  return (
    <div
      role="group"
      data-slot="input-group-addon"
      data-align={align}
      className={cn(inputGroupAddonVariants({ align }), className)}
      onClick={(e) => {
        if ((e.target as HTMLElement).closest('button')) {
          return;
        }
        e.currentTarget.parentElement?.querySelector('input')?.focus();
      }}
      {...props}
    />
  );
};

const InputGroupText = ({ className, ...props }: React.ComponentProps<'span'>) => {
  return (
    <span
      className={cn(
        'flex items-center gap-2',
        'text-muted-foreground text-sm',
        "[&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none",
        className
      )}
      {...props}
    />
  );
};

const InputGroupInput = ({ className, ...props }: React.ComponentProps<typeof Input>) => {
  return (
    <Input
      data-slot="input-group-control"
      className={cn(
        'h-auto min-w-0 flex-1 rounded-none border-0 bg-transparent py-0',
        'focus-visible:ring-0 aria-invalid:ring-0',
        className
      )}
      {...props}
    />
  );
};

const inputGroupButtonVariants = cva('flex items-center gap-2 shadow-none', {
  variants: {
    size: {
      xs: "h-6 gap-1 px-1.5 [&>svg:not([class*='size-'])]:size-3.5",
      sm: '',
      'icon-xs': 'size-6 p-0 has-[>svg]:p-0',
      'icon-sm': 'p-0 has-[>svg]:p-0',
    },
  },
  defaultVariants: {
    size: 'xs',
  },
});

const InputGroupButton = ({
  className,
  type = 'button',
  variant = 'ghost',
  size = 'xs',
  ...props
}: Omit<React.ComponentProps<typeof Button>, 'size' | 'type'> &
  VariantProps<typeof inputGroupButtonVariants> & {
    type?: 'button' | 'submit' | 'reset';
  }) => {
  return (
    <Button
      type={type}
      size={size}
      data-slot="input-group-button"
      data-size={size}
      variant={variant}
      className={cn(inputGroupButtonVariants({ size }), className)}
      {...props}
    />
  );
};

export { InputGroup, InputGroupAddon, InputGroupButton, InputGroupInput, InputGroupText };
